/**
 * auth_tabs_controller.js — the Sign in / Create account switch (Stimulus).
 *
 * Scoped to the card of templates/auth/entry.html, which renders BOTH forms, so
 * a tab tap only swaps which panel is visible; nothing waits on the server.
 * The address and title follow the tab via replaceState, keeping history.state
 * (Turbo's restoration id): a refresh reopens the same tab, and Back leaves the
 * screen rather than stepping through tab switches. Each tab is a real link, so
 * without this controller a tap still navigates to that URL.
 *
 * An email typed on one tab is carried to the other when that field is empty —
 * someone who starts signing in and realises they need an account keeps it.
 */

import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
    static targets = ["tab", "panel", "email"];

    select(event) {
        event.preventDefault();
        const tab = event.currentTarget;
        const name = tab.dataset.tab;
        if (tab.getAttribute("aria-selected") === "true") return;

        const typedEmail = this._visibleEmail();
        this._show(name);
        this._carryEmail(name, typedEmail);

        const url = new URL(tab.href);
        url.search = window.location.search;
        history.replaceState(history.state, "", url);
        document.title = tab.dataset.title;
    }

    _show(name) {
        this.tabTargets.forEach((tab) => {
            const active = tab.dataset.tab === name;
            tab.classList.toggle("active", active);
            tab.setAttribute("aria-selected", String(active));
        });
        this.panelTargets.forEach((panel) => {
            panel.hidden = panel.dataset.tab !== name;
        });
    }

    _visibleEmail() {
        const field = this.emailTargets.find((input) => !input.closest("[hidden]"));
        return field ? field.value.trim() : "";
    }

    _carryEmail(name, value) {
        if (!value) return;
        const panel = this.panelTargets.find((p) => p.dataset.tab === name);
        const field = this.emailTargets.find((input) => panel.contains(input));
        if (field && !field.value) field.value = value;
    }
}
